import { z } from "zod";

import { NotFoundError } from "../../types";
import type { IdentityApi, InvitationPreview } from "../types";
import type { LiveCtx } from "./auth";
import { IdentityServiceError } from "./errors";
import { ROUTES } from "./routes";
import { parseSession, parser, role, type Parser } from "./schemas";

type InvitationLinkApi = Pick<
  IdentityApi,
  "getInvitation" | "acceptInvitation" | "declineInvitation"
>;

const previewSchema = z.object({
  status: z.enum(["pending", "accepted", "declined", "revoked", "expired"]),
  email: z.string(),
  role,
  organisationName: z.string(),
  inviterName: z.string(),
  expiresAt: z.string(),
});

export const parsePreview: Parser<InvitationPreview> = parser(previewSchema);

/** An unknown token is the page's "isn't valid" case, as the mock answers it. */
function unknownToken(error: unknown): never {
  if (error instanceof IdentityServiceError && error.code === "not_found")
    throw new NotFoundError("Invitation");
  throw error;
}

/**
 * Organisation invitation links (`/invite/<token>`; FR-ADMIN-026, FR-PROJ-115) on the identity service, which opens
 * them through the WorkOS Invitations API. Anyone with the link sees and may decline it; the invited person accepts
 * it signed in, and the reply is their session, now in the organisation.
 */
export function createInvitationLinkApi(ctx: LiveCtx): InvitationLinkApi {
  const { http, signals } = ctx;
  return {
    getInvitation: (token) =>
      http.get(ROUTES.invitationLink(token), parsePreview).catch(unknownToken),
    async acceptInvitation(token) {
      const session = await http
        .send("POST", ROUTES.invitationLinkAction(token, "accept"), {}, parseSession)
        .catch(unknownToken);
      ctx.remember(session);
      signals.announce();
      return session;
    },
    declineInvitation: (token) =>
      http.sendOnly("POST", ROUTES.invitationLinkAction(token, "decline"), {}).catch(unknownToken),
  };
}
